/* eslint-disable no-control-regex */
/* =================== INPUT SANITIZATION UTILITIES =================== */
/*
 * PROTECTS AGAINST:
 * - XSS ATTACKS (CROSS-SITE SCRIPTING)
 * - UNICODE NORMALIZATION ATTACKS
 * - ZERO-WIDTH CHARACTERS
 * - BIDI OVERRIDE ATTACKS
 * - LINE/PARAGRAPH SEPARATOR INJECTION
 * - EXCESSIVE WHITESPACE
 * - CONTROL CHARACTERS
 * - DOS ATTACKS (LENGTH LIMITS)
 *
 * BEST PRACTICE: ALWAYS SANITIZE USER INPUT BEFORE:
 * 1. STORING IN DATABASE
 * 2. DISPLAYING IN UI
 * 3. USING IN SEARCH/FILTER OPERATIONS
 * 4. SENDING TO BACKEND API
 */

import { INPUT_LIMITS } from '../constants';

/**
 * SANITIZE TEXT INPUT (NAMES, TITLES, SHORT TEXT)
 *
 * USE CASES:
 * - CUSTOMER NAMES
 * - PRODUCT NAMES
 * - TRANSACTION DESCRIPTIONS (SHORT)
 * - ANY SINGLE-LINE TEXT INPUT
 *
 * EXAMPLE:
 * INPUT:  "John\u200BDoe  " (contains zero-width space + extra spaces)
 * OUTPUT: "John Doe" (clean!)
 */
export function sanitizeTextInput(input: string, maxLength = INPUT_LIMITS.TEXT_SHORT): string {
  if (typeof input !== 'string') return '';
  if (input.length === 0) return '';

  return (
    input
      // NORMALIZE UNICODE TO PREVENT NORMALIZATION ATTACKS
      .normalize('NFKC')
      // REMOVE ZERO-WIDTH CHARACTERS (INVISIBLE CHARS)
      .replace(/[\u200B-\u200D\uFEFF]/g, '')
      // REMOVE BIDI OVERRIDE CHARACTERS (PREVENT TEXT DIRECTION ATTACKS)
      .replace(/[\u202A-\u202E\u2066-\u2069]/g, '')
      // REMOVE LINE/PARAGRAPH SEPARATORS (PREVENT JSON INJECTION)
      .replace(/[\u2028\u2029]/g, '')
      // REMOVE CONTROL CHARACTERS (NON-PRINTABLE)
      .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
      // TRIM WHITESPACE
      .trim()
      // COLLAPSE MULTIPLE SPACES INTO ONE
      .replace(/\s+/g, ' ')
      // LIMIT LENGTH TO PREVENT DOS
      .slice(0, maxLength)
  );
}

/**
 * SANITIZE EMAIL INPUT
 *
 * USE CASES:
 * - EMAIL ADDRESSES
 * - LOGIN USERNAMES (IF EMAIL-BASED)
 *
 * EXAMPLE:
 * INPUT:  "  User@EXAMPLE.com  "
 * OUTPUT: "user@example.com"
 */
export function sanitizeEmail(input: string, maxLength = INPUT_LIMITS.EMAIL): string {
  if (typeof input !== 'string') return '';
  if (input.length === 0) return '';

  return (
    input
      .normalize('NFKC')
      .replace(/[\u200B-\u200D\uFEFF]/g, '')
      .replace(/[\u202A-\u202E\u2066-\u2069]/g, '')
      .replace(/[\u2028\u2029]/g, '')
      // REMOVE ALL WHITESPACE (EMAILS HAVE NO SPACES)
      .replace(/\s/g, '')
      // REMOVE CONTROL CHARACTERS
      .replace(/[\x00-\x1F\x7F]/g, '')
      // CONVERT TO LOWERCASE FOR CONSISTENCY
      .toLowerCase()
      // LIMIT LENGTH (RFC 5321 MAX EMAIL LENGTH IS 254)
      .slice(0, maxLength)
  );
}

/**
 * SANITIZE PHONE NUMBER
 *
 * USE CASES:
 * - PHONE NUMBER INPUTS
 * - FAX NUMBERS
 *
 * KEEPS: NUMBERS, +, -, (, ), SPACES
 * REMOVES: LETTERS, SPECIAL CHARS
 * VALIDATES: MINIMUM DIGITS REQUIRED
 *
 * EXAMPLE:
 * INPUT:  "+1 (555) 123-4567 ext 890"
 * OUTPUT: "+1 (555) 123-4567"
 */
export function sanitizePhone(input: string, maxLength = INPUT_LIMITS.PHONE): string {
  if (typeof input !== 'string') return '';
  if (input.length === 0) return '';

  const sanitized = input
    .normalize('NFKC')
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .replace(/[\u202A-\u202E\u2066-\u2069]/g, '')
    .replace(/[\u2028\u2029]/g, '')
    // KEEP ONLY NUMBERS, +, -, (, ), AND SPACES
    .replace(/[^0-9+\-()\s]/g, '')
    .trim()
    .slice(0, maxLength);

  // VALIDATE: MUST CONTAIN AT LEAST 3 DIGITS TO BE A VALID PHONE NUMBER
  const digitCount = (sanitized.match(/\d/g) || []).length;
  if (digitCount < INPUT_LIMITS.PHONE_MIN_DIGITS) {
    return '';
  }

  // VALIDATE: CANNOT START WITH MULTIPLE + OR SPECIAL CHARS
  if (/^[+\-()]{3,}/.test(sanitized)) {
    return '';
  }

  return sanitized;
}

/**
 * SANITIZE NUMERIC INPUT
 *
 * USE CASES:
 * - PRICE/AMOUNT INPUTS
 * - QUANTITY INPUTS
 * - PERCENTAGE INPUTS
 *
 * EXAMPLE:
 * INPUT:  "$1,234.56abc"
 * OUTPUT: "1234.56"
 */
export function sanitizeNumeric(input: string, allowNegative = false): string {
  if (typeof input !== 'string') return '';
  if (input.length === 0) return '';

  let sanitized = input
    .normalize('NFKC')
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .replace(/[\u202A-\u202E\u2066-\u2069]/g, '')
    .replace(/[\u2028\u2029]/g, '')
    // REMOVE WHITESPACE
    .replace(/\s/g, '');

  if (allowNegative) {
    // KEEP ONLY NUMBERS, DECIMAL POINT, AND MINUS SIGN
    sanitized = sanitized.replace(/[^0-9.-]/g, '');

    // HANDLE NEGATIVE SIGN: ONLY ALLOW AT THE VERY START
    const firstChar = sanitized.charAt(0);
    const hasNegative = firstChar === '-';

    // REMOVE ALL MINUS SIGNS
    sanitized = sanitized.replace(/-/g, '');

    // ADD BACK ONE MINUS AT START IF ORIGINAL STARTED WITH IT
    if (hasNegative && sanitized.length > 0) {
      sanitized = `-${sanitized}`;
    }
  } else {
    // KEEP ONLY NUMBERS AND DECIMAL POINT
    sanitized = sanitized.replace(/[^0-9.]/g, '');
  }

  // ENSURE ONLY ONE DECIMAL POINT (KEEP FIRST ONE, REMOVE REST)
  const parts = sanitized.split('.');
  if (parts.length > 2) {
    // EXAMPLE: "1.2.3.4" becomes "1.234"
    sanitized = `${parts[0]}.${parts.slice(1).join('')}`;
  }

  // REMOVE LEADING ZEROS EXCEPT WHEN NUMBER IS 0.XXX OR JUST 0
  if (sanitized.length > 1 && sanitized.startsWith('0') && !sanitized.startsWith('0.')) {
    sanitized = sanitized.replace(/^0+/, '');
  }

  // HANDLE EDGE CASE: INPUT WAS JUST "." OR "0."
  if (sanitized === '.' || sanitized === '') {
    return '';
  }

  // LIMIT LENGTH TO PREVENT OVERFLOW
  return sanitized.slice(0, 50);
}

/**
 * SANITIZE MULTILINE TEXT (DESCRIPTIONS, COMMENTS, NOTES)
 *
 * USE CASES:
 * - TRANSACTION DESCRIPTIONS (LONG)
 * - CUSTOMER NOTES
 * - PRODUCT DESCRIPTIONS
 * - COMMENTS
 *
 * PRESERVES: NEWLINES, LINE STRUCTURE
 * REMOVES: DANGEROUS CHARACTERS, EXCESSIVE BLANK LINES
 *
 * EXAMPLE:
 * INPUT:  "Line 1\n\n\n\nLine 2  "
 * OUTPUT: "Line 1\n\nLine 2"
 */
export function sanitizeMultilineText(input: string, maxLength = INPUT_LIMITS.TEXT_LONG): string {
  if (typeof input !== 'string') return '';
  if (input.length === 0) return '';

  return (
    input
      .normalize('NFKC')
      .replace(/[\u200B-\u200D\uFEFF]/g, '')
      .replace(/[\u202A-\u202E\u2066-\u2069]/g, '')
      // NOTE: PRESERVE \u2028 AND \u2029 AFTER NORMALIZATION TO \n
      .replace(/[\u2028\u2029]/g, '\n')
      // REMOVE CONTROL CHARACTERS EXCEPT NEWLINE, CR, AND TAB
      .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
      // NORMALIZE LINE ENDINGS TO \n
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      // TRIM EACH LINE
      .split('\n')
      .map((line) => line.trim())
      .join('\n')
      .trim()
      // REMOVE EXCESSIVE BLANK LINES (MORE THAN 2)
      .replace(/\n{3,}/g, '\n\n')
      // LIMIT LENGTH TO PREVENT DOS
      .slice(0, maxLength)
  );
}

/**
 * SANITIZE SEARCH QUERY
 *
 * USE CASES:
 * - SEARCH INPUTS
 * - FILTER INPUTS
 * - AUTO-COMPLETE QUERIES
 *
 * EXAMPLE:
 * INPUT:  "  search   term  "
 * OUTPUT: "search term"
 */
export function sanitizeSearchQuery(input: string, maxLength = INPUT_LIMITS.SEARCH_QUERY): string {
  if (typeof input !== 'string') return '';
  if (input.length === 0) return '';

  return (
    input
      .normalize('NFKC')
      .replace(/[\u200B-\u200D\uFEFF]/g, '')
      .replace(/[\u202A-\u202E\u2066-\u2069]/g, '')
      .replace(/[\u2028\u2029]/g, '')
      // eslint-disable-next-line no-control-regex
      .replace(/[\x00-\x1F\x7F]/g, '')
      // ONLY TRIM START TO ALLOW TYPING SPACES BETWEEN WORDS
      .trimStart()
      .replace(/\s+/g, ' ')
      // LIMIT LENGTH TO PREVENT ABUSE
      .slice(0, maxLength)
  );
}

/**
 * ESCAPE HTML SPECIAL CHARACTERS
 *
 * USE CASES:
 * - BEFORE RENDERING USER-GENERATED CONTENT IN HTML
 * - PREVENTING XSS ATTACKS
 *
 * EXAMPLE:
 * INPUT:  "<script>alert('xss')</script>"
 * OUTPUT: "&lt;script&gt;alert('xss')&lt;/script&gt;"
 */
export function escapeHtml(input: string): string {
  if (typeof input !== 'string') return '';
  if (input.length === 0) return '';

  const htmlEscapeMap: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '/': '&#x2F;',
  };

  // CRITICAL: ESCAPE & FIRST TO AVOID DOUBLE-ESCAPING
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
}

/**
 * CHECK IF STRING CONTAINS ONLY SAFE CHARACTERS
 *
 * USE CASES:
 * - VALIDATION BEFORE DATABASE INSERT
 * - SECURITY CHECKS
 *
 * RETURNS: TRUE IF SAFE, FALSE IF CONTAINS DANGEROUS CHARS
 */
export function isSafeString(input: string): boolean {
  if (typeof input !== 'string') return false;
  if (input.length === 0) return true;

  // CHECK FOR CONTROL CHARACTERS (EXCEPT NEWLINE, CR, TAB)
  const hasControlChars = /[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/.test(input);

  // CHECK FOR ZERO-WIDTH CHARACTERS
  const hasZeroWidth = /[\u200B-\u200D\uFEFF]/.test(input);

  // CHECK FOR BIDI OVERRIDE CHARACTERS
  const hasBidiOverride = /[\u202A-\u202E\u2066-\u2069]/.test(input);

  // CHECK FOR LINE/PARAGRAPH SEPARATORS
  const hasLineSeparators = /[\u2028\u2029]/.test(input);

  // CHECK FOR SURROGATE PAIRS (POTENTIAL ATTACK)
  const hasSurrogates = /[\uD800-\uDFFF]/.test(input);

  return (
    !hasControlChars && !hasZeroWidth && !hasBidiOverride && !hasLineSeparators && !hasSurrogates
  );
}
